import { SecurePreferences } from '@capawesome-team/capacitor-secure-preferences';
import { ErrorCode as VaultErrorCode, Vault, VaultType } from '@capawesome-team/capacitor-vault';

import { environment } from '../../environment/environment';
import { isNative } from '../../helpers/utils.helper';
import { RaygunErrorHandlerService } from '../../services/raygun.service';

const { logError } = RaygunErrorHandlerService();

const VAULT_ID = 'com.trycycledata';

const promptConfig = {
  cancelButtonText: 'Cancel',
  title: 'Sign in to TetherAll',
  subtitle: 'Use your face or fingerprint to sign in securely without typing your password.',
};

export const DeviceSecurityType = {
  None: 'None',
  Biometrics: 'Biometrics',
} as const;

export type DeviceSecurityType = (typeof DeviceSecurityType)[keyof typeof DeviceSecurityType];

export type VaultPluginError = {
  code?: string;
  message?: string;
};

const vaultOptions = {
  vaultId: VAULT_ID,
  type: VaultType.Biometric,
  lockAfterBackgrounded: 5000,
  ...promptConfig,
};

export const logVaultError = (e: unknown, source: string) => {
  const error = e as VaultPluginError;
  logError(e, ['loginSlice', source, 'VaultError', `code: ${error.code}`, `message: ${error.message}`]);
};

let vaultInitialization: Promise<void> | null = null;

// Capawesome Vault must be initialized once per app session before any other method,
// even when the vault was created in a previous session (otherwise calls reject with VAULT_NOT_FOUND)
const initializeVault = (): Promise<void> => {
  vaultInitialization ??= Vault.initialize(vaultOptions).catch((e) => {
    vaultInitialization = null;
    throw e;
  });
  return vaultInitialization;
};

const destroyVault = async (): Promise<void> => {
  vaultInitialization = null;
  await Vault.destroy({ vaultId: VAULT_ID });
};

const vaultExists = async (): Promise<boolean> => {
  if (!isNative()) {
    return false;
  }
  const { exists } = await Vault.exists({ vaultId: VAULT_ID });
  return exists;
};

export const getPersistedLockType = async (): Promise<DeviceSecurityType> => {
  return (await vaultExists()) ? DeviceSecurityType.Biometrics : DeviceSecurityType.None;
};

const isVaultActive = async (): Promise<boolean> => {
  if (!(await vaultExists())) {
    return false;
  }
  await initializeVault();
  return true;
};

export const isBiometricVaultEmpty = async (): Promise<boolean> => {
  try {
    if (!(await isVaultActive())) {
      return true;
    }
    const { isEmpty } = await Vault.isEmpty({ vaultId: VAULT_ID });
    return isEmpty;
  } catch (e) {
    logVaultError(e, 'isBiometricVaultEmpty');
    return true;
  }
};

// reads from the biometric vault when enabled, otherwise from secure preferences
export const getStoredAuthResult = async (): Promise<string | null> => {
  if (!(await isVaultActive())) {
    const { value } = await SecurePreferences.get({ key: environment.vaultKey });
    return value;
  }

  const { isEmpty } = await Vault.isEmpty({ vaultId: VAULT_ID });
  if (isEmpty) {
    return null;
  }

  await Vault.unlock({ vaultId: VAULT_ID });
  const { value } = await Vault.getValue({ key: environment.vaultKey, vaultId: VAULT_ID });
  return value;
};

export const setStoredAuthResult = async (value: string): Promise<void> => {
  if (await isVaultActive()) {
    await Vault.unlock({ vaultId: VAULT_ID });
    await Vault.setValue({ key: environment.vaultKey, value, vaultId: VAULT_ID });
    return;
  }
  await SecurePreferences.set({ key: environment.vaultKey, value });
};

export const removeStoredAuthResult = async (lockVault: boolean): Promise<void> => {
  if (!(await isVaultActive())) {
    await SecurePreferences.remove({ key: environment.vaultKey });
    return;
  }

  await Vault.unlock({ vaultId: VAULT_ID }).catch(() => undefined);
  await Vault.removeValue({ key: environment.vaultKey, vaultId: VAULT_ID }).catch(() => undefined);
  if (lockVault) {
    await Vault.lock({ vaultId: VAULT_ID });
  }
};

export const getStoredLogin = async (): Promise<string | null> => {
  if (!(await isVaultActive())) {
    return null;
  }
  await Vault.unlock({ vaultId: VAULT_ID });
  const { value } = await Vault.getValue({ key: environment.loginKey, vaultId: VAULT_ID });
  return value;
};

export const setStoredLogin = async (value: string): Promise<void> => {
  await Vault.unlock({ vaultId: VAULT_ID });
  await Vault.setValue({ key: environment.loginKey, value, vaultId: VAULT_ID });
};

const enableBiometricVault = async (): Promise<void> => {
  try {
    await initializeVault();
  } catch (e) {
    if ((e as VaultPluginError).code !== VaultErrorCode.KeyInvalidated) {
      throw e;
    }
    // stored values can no longer be decrypted; start over with a fresh vault
    await destroyVault();
    await initializeVault();
  }
  await Vault.unlock({ vaultId: VAULT_ID });

  const { value: storedAuthResult } = await SecurePreferences.get({ key: environment.vaultKey });
  if (storedAuthResult) {
    await Vault.setValue({ key: environment.vaultKey, value: storedAuthResult, vaultId: VAULT_ID });
    await SecurePreferences.remove({ key: environment.vaultKey });
  }
};

const disableBiometricVault = async (): Promise<void> => {
  if (!(await vaultExists())) {
    return;
  }

  try {
    await initializeVault();
  } catch (e) {
    if ((e as VaultPluginError).code !== VaultErrorCode.KeyInvalidated) {
      throw e;
    }
    // stored values can no longer be decrypted, so there is nothing to carry over
    await destroyVault();
    return;
  }

  const { isLocked } = await Vault.isLocked({ vaultId: VAULT_ID });
  if (isLocked) {
    await Vault.unlock({ vaultId: VAULT_ID });
  }

  const { value: storedAuthResult } = await Vault.getValue({ key: environment.vaultKey, vaultId: VAULT_ID });
  if (storedAuthResult) {
    await SecurePreferences.set({ key: environment.vaultKey, value: storedAuthResult });
  }
  await destroyVault();
};

export const setVaultLockType = async (lockType: DeviceSecurityType): Promise<void> => {
  if (lockType === DeviceSecurityType.Biometrics) {
    await enableBiometricVault();
  } else {
    await disableBiometricVault();
  }
};
